<template>
    <div>
        <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
            <!-- 탭 헤더 -->
            <v-toolbar color="grey lighten-5" flat class="elevation-1" dense>
                <v-tabs
                    v-model="activeTab"
                    color="primary"
                    slider-color="primary"
                    background-color="transparent"
                    icons-and-text
                    class="flex-grow-1"
                    show-arrows
                >
                    <v-tab class="tab-item">
                        <span class="tab-text">이벤트 설정</span>
                        <v-icon class="ml-2" size="20">mdi-slot-machine</v-icon>
                    </v-tab>
                    <v-tab class="tab-item">
                        <span class="tab-text">회차별 정보</span>
                        <v-icon class="ml-2" size="20">mdi-format-list-numbered</v-icon>
                    </v-tab>
                </v-tabs>
            </v-toolbar>

            <v-tabs-items v-model="activeTab">
                <!-- =============================== -->
                <!-- 탭 1: 이벤트 설정 -->
                <!-- =============================== -->
                <v-tab-item>
                    <v-card-text class="pa-4">
                        <!-- 1. 기본 설정 -->
                        <v-card outlined class="mb-3">
                            <v-card-subtitle class="py-2 font-weight-bold" style="background:#E3F2FD">
                                <v-icon left size="18">mdi-cog</v-icon>
                                기본 설정
                            </v-card-subtitle>
                            <v-card-text class="pa-3">
                                <v-row dense>
                                    <v-col cols="12" sm="4">
                                        <v-select
                                            label="이벤트 상태"
                                            v-model="eventConfig.status"
                                            :items="statusOptions"
                                            item-text="name"
                                            item-value="value"
                                            dense outlined hide-details
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-text-field label="제한시간 (초)" v-model.number="eventConfig.time_limit" type="number" dense outlined hide-details></v-text-field>
                                        <v-btn-toggle v-model="timeLimitPreset" dense class="mt-1 flex-wrap" style="width: 100%;">
                                            <v-btn x-small :value="3600">1시간</v-btn>
                                            <v-btn x-small :value="21600">6시간</v-btn>
                                            <v-btn x-small :value="43200">12시간</v-btn>
                                            <v-btn x-small :value="86400">24시간</v-btn>
                                        </v-btn-toggle>
                                    </v-col>
                                </v-row>
                                <v-row dense class="mt-2">
                                    <v-col cols="12" sm="6">
                                        <v-text-field label="시작일시" v-model="eventConfig.start_dt" type="datetime-local" dense outlined hide-details></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field label="종료일시" v-model="eventConfig.end_dt" type="datetime-local" dense outlined hide-details></v-text-field>
                                    </v-col>
                                </v-row>
                                <div v-if="eventConfig.time_limit" class="mt-1 text-caption grey--text">
                                    설정된 제한시간: {{ formatTimeLimit(eventConfig.time_limit) }}
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- 2. 골드 설정 (판돈/수수료) -->
                        <v-card outlined class="mb-3">
                            <v-card-subtitle class="py-2 font-weight-bold" style="background:#FFF3E0">
                                <v-icon left size="18">mdi-gold</v-icon>
                                골드 설정
                            </v-card-subtitle>
                            <v-card-text class="pa-3">
                                <v-row dense>
                                    <v-col cols="12" sm="6">
                                        <v-text-field label="판돈 골드 (g)" v-model.number="eventConfig.bet_gold" type="number" step="0.01" dense outlined hide-details hint="현재 기획: 1g" persistent-hint></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field label="수수료 골드 (g)" v-model.number="eventConfig.fee_gold" type="number" step="0.01" dense outlined hide-details hint="현재 기획: 0.05g" persistent-hint></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <!-- 3. 돌림판 딱지 관리 -->
                        <v-card outlined class="mb-3">
                            <v-card-subtitle class="py-2 font-weight-bold" style="background:#F3E5F5">
                                <v-icon left size="18">mdi-cards</v-icon>
                                돌림판 딱지 관리
                                <v-chip small class="ml-3" color="deep-purple lighten-4">합계: {{ rouletteGramTotal }}g</v-chip>
                                <v-btn x-small color="primary" class="ml-2" @click="addRouletteItem">
                                    <v-icon left x-small>mdi-plus</v-icon>추가
                                </v-btn>
                            </v-card-subtitle>
                            <v-card-text class="pa-3">
                                <v-alert dense type="info" text class="mb-3">
                                    이름이 지정되지 않은 항목은 자동으로 <strong>해파리</strong> 등급으로 처리됩니다.
                                </v-alert>
                                <div style="overflow-x: auto;">
                                    <v-data-table
                                        :headers="rouletteItemHeaders"
                                        :items="eventConfig.roulette_items"
                                        hide-default-footer
                                        disable-pagination
                                        dense
                                        class="elevation-1"
                                    >
                                        <template v-slot:item.gram="{ item }">
                                            <v-text-field v-model.number="item.gram" type="number" step="0.01" suffix="g" dense hide-details single-line></v-text-field>
                                        </template>
                                        <template v-slot:item.name="{ item }">
                                            <v-text-field v-model="item.name" :placeholder="'해파리'" dense hide-details single-line></v-text-field>
                                        </template>
                                        <template v-slot:item.tag="{ item }">
                                            <v-text-field v-model="item.tag" placeholder="예: WHALE" dense hide-details single-line style="text-transform: uppercase;"></v-text-field>
                                        </template>
                                        <template v-slot:item.display_name="{ item }">
                                            <v-chip :color="item.name ? 'primary' : 'grey'" small dark>
                                                {{ item.name || '해파리' }}
                                            </v-chip>
                                        </template>
                                        <template v-slot:item.actions="{ item }">
                                            <v-btn icon small color="red" @click="removeRouletteItem(item)">
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-data-table>
                                </div>
                            </v-card-text>
                        </v-card>

                        <div class="d-flex justify-end mt-3" style="gap: 8px;">
                            <v-btn outlined color="grey darken-1" @click="openJsonLink">
                                <v-icon left>mdi-link-variant</v-icon>
                                링크보기
                            </v-btn>
                            <v-btn color="primary" :loading="saving" @click="saveEventConfig">
                                <v-icon left>mdi-content-save</v-icon>
                                설정 저장
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-tab-item>

                <!-- =============================== -->
                <!-- 탭 2: 회차별 정보 -->
                <!-- =============================== -->
                <v-tab-item>
                    <v-card-text class="pa-4">
                        <v-toolbar flat dense class="mb-3 flex-wrap" style="height: auto;">
                            <v-toolbar-title class="text-subtitle-1 font-weight-bold">회차별 정보</v-toolbar-title>
                            <v-spacer></v-spacer>
                            <v-btn small color="primary" @click="loadRounds(); loadCurrentRound();">
                                <v-icon left small>mdi-refresh</v-icon>새로고침
                            </v-btn>
                        </v-toolbar>

                        <!-- 현재 진행중 회차 요약 -->
                        <v-alert v-if="currentRoundInfo" type="info" text dense class="mb-3">
                            <div class="d-flex flex-wrap align-center" style="gap: 12px;">
                                <strong>현재 {{ currentRoundInfo.ROUND_NO }}회차</strong>
                                <v-chip :color="getRoundStatusColor(currentRoundInfo.STATUS)" x-small dark>
                                    {{ getRoundStatusText(currentRoundInfo.STATUS) }}
                                </v-chip>
                                <span>참여: {{ currentRoundInfo.TOTAL_PARTICIPANTS || 0 }}명</span>
                                <span>풀: {{ currentRoundInfo.TOTAL_POOL || 0 }}g</span>
                            </div>
                        </v-alert>

                        <div style="overflow-x: auto;">
                            <v-data-table
                                :headers="roundHeaders"
                                :items="rounds"
                                :loading="loadingRounds"
                                dense
                                class="elevation-1"
                                :items-per-page="10"
                            >
                                <template v-slot:item.STATUS="{ item }">
                                    <v-chip :color="getRoundStatusColor(item.STATUS)" small dark>
                                        {{ getRoundStatusText(item.STATUS) }}
                                    </v-chip>
                                </template>
                                <template v-slot:item.TOTAL_PARTICIPANTS="{ item }">
                                    {{ (item.TOTAL_PARTICIPANTS || 0).toLocaleString() }}명
                                </template>
                                <template v-slot:item.TOTAL_POOL="{ item }">
                                    {{ (item.TOTAL_POOL || 0) }}g
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon small @click="openRoundDetail(item)">
                                        <v-icon small>mdi-eye</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </div>
                    </v-card-text>

                    <!-- 회차 상세 다이얼로그 -->
                    <v-dialog v-model="roundDetailDialog" max-width="800" :fullscreen="$vuetify.breakpoint.xsOnly">
                        <v-card v-if="selectedRound">
                            <v-card-title>
                                <v-icon left>mdi-information</v-icon>
                                {{ selectedRoundInfo.ROUND_NO }}회차 상세
                                <v-spacer></v-spacer>
                                <v-btn icon @click="roundDetailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                            </v-card-title>
                            <v-card-text>
                                <v-row dense>
                                    <v-col cols="12" sm="4">
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-subtitle>상태</v-list-item-subtitle>
                                                <v-list-item-title>
                                                    <v-chip :color="getRoundStatusColor(selectedRoundInfo.STATUS)" small dark>
                                                        {{ getRoundStatusText(selectedRoundInfo.STATUS) }}
                                                    </v-chip>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-subtitle>참여자</v-list-item-subtitle>
                                                <v-list-item-title>{{ (selectedRoundInfo.TOTAL_PARTICIPANTS || 0).toLocaleString() }}명</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-subtitle>총 풀</v-list-item-subtitle>
                                                <v-list-item-title>{{ selectedRoundInfo.TOTAL_POOL || 0 }}g</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-subtitle>수수료 합계</v-list-item-subtitle>
                                                <v-list-item-title>{{ selectedRoundInfo.TOTAL_FEE || 0 }}g</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-subtitle>시작일</v-list-item-subtitle>
                                                <v-list-item-title>{{ selectedRoundInfo.CREATE_DT }}</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-subtitle>종료일</v-list-item-subtitle>
                                                <v-list-item-title>{{ selectedRoundInfo.COMPLETED_DT || '-' }}</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                </v-row>
                                <v-divider class="my-3"></v-divider>
                                <div class="text-subtitle-2 font-weight-bold mb-2">참여자 목록 ({{ selectedRoundEntries.length }}명)</div>
                                <div style="overflow-x: auto;">
                                    <v-data-table
                                        :headers="entryHeaders"
                                        :items="selectedRound.entries || []"
                                        dense
                                        class="elevation-1"
                                        :items-per-page="10"
                                    >
                                        <template v-slot:item.REWARD_GRAM="{ item }">
                                            <span v-if="item.REWARD_GRAM" class="font-weight-bold green--text">{{ item.REWARD_GRAM }}g</span>
                                            <span v-else class="grey--text">-</span>
                                        </template>
                                        <template v-slot:item.REWARD_NAME="{ item }">
                                            <v-chip v-if="item.REWARD_NAME" small color="primary" dark>{{ item.REWARD_NAME }}</v-chip>
                                            <span v-else class="grey--text">-</span>
                                        </template>
                                    </v-data-table>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </v-tab-item>
            </v-tabs-items>
        </v-card>
    </div>
</template>

<script>
import axios from 'axios';
import config from '../../config/config';

export default {
    data: () => ({
        activeTab: 0,
        saving: false,

        // 이벤트 설정
        eventConfig: {
            status: 'N',
            start_dt: '',
            end_dt: '',
            time_limit: 3600,
            bet_gold: 1,
            fee_gold: 0.05,
            daily_limit: 0,
            roulette_items: [
                { gram: 0.25, name: '흰수염고래', tag: 'WHALE' },
                { gram: 0.2, name: '백상아리', tag: 'SHARK' },
                { gram: 0.15, name: '대왕오징어', tag: 'SQUID' },
                { gram: 0.1, name: '타이거새우', tag: 'SHRIMP' },
                { gram: 0.07, name: '장수거북', tag: 'TURTLE' },
                { gram: 0.04, name: '', tag: 'JELLY_04' },
                { gram: 0.03, name: '', tag: 'JELLY_03' },
                { gram: 0.02, name: '', tag: 'JELLY_02' },
                { gram: 0.01, name: '', tag: 'JELLY_01' }
            ]
        },

        statusOptions: [
            { name: '비활성', value: 'N' },
            { name: '활성', value: 'Y' }
        ],

        rouletteItemHeaders: [
            { text: '그람수', value: 'gram', width: '20%' },
            { text: '이름 (빈칸=해파리)', value: 'name', width: '25%' },
            { text: '태그', value: 'tag', width: '20%' },
            { text: '등급', value: 'display_name', width: '20%' },
            { text: '', value: 'actions', width: '15%', sortable: false }
        ],

        // 회차별 정보
        rounds: [],
        loadingRounds: false,
        roundDetailDialog: false,
        selectedRound: null,
        currentRound: null,


        roundHeaders: [
            { text: '회차', value: 'ROUND_NO', width: '10%' },
            { text: '상태', value: 'STATUS', width: '12%' },
            { text: '시작일', value: 'CREATE_DT', width: '22%' },
            { text: '종료일', value: 'COMPLETED_DT', width: '22%' },
            { text: '참여자', value: 'TOTAL_PARTICIPANTS', width: '14%' },
            { text: '총 풀', value: 'TOTAL_POOL', width: '14%' },
            { text: '', value: 'actions', width: '6%', sortable: false }
        ],

        entryHeaders: [
            { text: '유저ID', value: 'USER_ID' },
            { text: '닉네임', value: 'NICKNAME' },
            { text: '판돈', value: 'BET_GOLD' },
            { text: '수수료', value: 'FEE_GOLD' },
            { text: '당첨 그람', value: 'REWARD_GRAM' },
            { text: '당첨 딱지', value: 'REWARD_NAME' },
            { text: '참여일', value: 'CREATE_DT' }
        ]
    }),

    computed: {
        rouletteGramTotal() {
            return Math.round(this.eventConfig.roulette_items.reduce((sum, r) => sum + (r.gram || 0), 0) * 1000) / 1000;
        },
        currentRoundInfo() {
            if (!this.currentRound) return null;
            return this.currentRound.round_info || this.currentRound;
        },
        selectedRoundInfo() {
            if (!this.selectedRound) return null;
            return this.selectedRound.round_info || this.selectedRound;
        },
        selectedRoundEntries() {
            if (!this.selectedRound) return [];
            return this.selectedRound.entries || [];
        },
        timeLimitPreset: {
            get() {
                const v = this.eventConfig.time_limit;
                if (v === 3600 || v === 21600 || v === 43200 || v === 86400) return v;
                return undefined;
            },
            set(val) {
                if (val != null) this.eventConfig.time_limit = val;
            }
        }
    },

    created() {
        this.loadEventConfig();
        this.loadRounds();
        this.loadCurrentRound();
    },

    methods: {
        // ---- 이벤트 설정 ----
        async loadEventConfig() {
            try {
                const env = this.$store.state.selected_game_package === 'dev' ? 'dev' : 'live';
                const timestamp = new Date().getTime();
                const url = `https://content-filecenter.s3.ap-northeast-2.amazonaws.com/carrygold/manage/${env}/RouletteEvent.json?t=${timestamp}`;
                const response = await fetch(url, { method: 'GET', cache: 'no-store', headers: { 'Cache-Control': 'no-cache, no-store' } });
                if (response.ok) {
                    const text = await response.text();
                    let data;
                    try {
                        data = JSON.parse(text);
                        // 이중 stringify 된 경우 한번 더 파싱
                        if (typeof data === 'string') {
                            data = JSON.parse(data);
                        }
                    } catch (parseErr) {
                        console.error('JSON 파싱 실패:', parseErr);
                        return;
                    }
                    // 구조 검증: 필수 키가 없거나 숫자 인덱스 키("0","1",...)가 있으면 깨진 데이터
                    if (typeof data !== 'object' || data === null || Array.isArray(data)
                        || data.hasOwnProperty('0') || !('status' in data || 'roulette_items' in data)) {
                        console.warn('S3 데이터가 깨져있음, 기본값 사용:', Object.keys(data).slice(0, 5));
                        return;
                    }
                    const defaults = {
                        status: 'N',
                        start_dt: '',
                        end_dt: '',
                        time_limit: 3600,
                        bet_gold: 1,
                        fee_gold: 0.05,
                        roulette_items: []
                    };
                    this.eventConfig = { ...defaults, ...data };
                }
            } catch (e) {
                console.log('룰렛 이벤트 설정 없음 (신규)', e);
            }
        },

        formatTimeLimit(seconds) {
            if (!seconds) return '';
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = seconds % 60;
            let parts = [];
            if (h > 0) parts.push(`${h}시간`);
            if (m > 0) parts.push(`${m}분`);
            if (s > 0) parts.push(`${s}초`);
            return parts.join(' ') || '0초';
        },

        async saveEventConfig() {
            this.saving = true;
            try {
                const env = this.$store.state.selected_game_package === 'dev' ? 'dev' : 'live';
                const { FileUpload_JSON } = require('../../util/fileutil');
                const filePath = `carrygold/manage/${env}/RouletteEvent.json`;
                const success = await FileUpload_JSON(this.eventConfig, filePath);
                if (success) {
                    try {
                        await axios.post('https://carrygoldapi.snapplay.io/carrygold/util/roulette_data_refresh');
                        console.log('[roulette_data_refresh] 호출 완료');
                    } catch (err) {
                        console.warn('[roulette_data_refresh] 호출 실패:', err?.response?.data ?? err.message);
                    }
                    alert('룰렛 이벤트 설정이 저장되었습니다.');
                    // S3 반영 대기 후 재로드
                    setTimeout(() => this.loadEventConfig(), 1500);
                }
            } catch (e) {
                console.error('저장 실패:', e);
                alert('저장에 실패했습니다.');
            }
            this.saving = false;
        },

        openJsonLink() {
            const env = this.$store.state.selected_game_package === 'dev' ? 'dev' : 'live';
            const url = `https://content-filecenter.s3.ap-northeast-2.amazonaws.com/carrygold/manage/${env}/RouletteEvent.json?t=${new Date().getTime()}`;
            window.open(url, '_blank');
        },

        addRouletteItem() {
            this.eventConfig.roulette_items.push({ gram: 0, name: '', tag: '' });
        },

        removeRouletteItem(item) {
            const idx = this.eventConfig.roulette_items.indexOf(item);
            if (idx > -1) this.eventConfig.roulette_items.splice(idx, 1);
        },

        // ---- 회차별 정보 ----
        async loadRounds() {
            this.loadingRounds = true;
            try {
                const res = await axios.post(`${config.getBackendUrl}/api/carrygold/roulette/rounds`, {
                    page: 1,
                    limit: 50
                });
                if (res.data) {
                    this.rounds = res.data.rounds || (res.data.result && res.data.result.rounds) || [];
                }
            } catch (e) {
                console.log('회차 데이터 조회 실패:', e);
                this.rounds = [];
            }
            this.loadingRounds = false;
        },

        async openRoundDetail(item) {
            try {
                const res = await axios.post(`${config.getBackendUrl}/api/carrygold/roulette/round_detail`, {
                    round_pid: item.PID
                });
                if (res.data) {
                    this.selectedRound = res.data.result || res.data;
                    this.roundDetailDialog = true;
                }
            } catch (e) {
                console.error('회차 상세 조회 실패:', e);
                alert('회차 상세 정보를 불러오지 못했습니다.');
            }
        },

        async loadCurrentRound() {
            try {
                const res = await axios.post(`${config.getBackendUrl}/api/carrygold/roulette/current`);
                if (res.data) {
                    this.currentRound = res.data.result || res.data;
                }
            } catch (e) {
                console.log('현재 회차 조회 실패:', e);
                this.currentRound = null;
            }
        },

        getRoundStatusColor(status) {
            const map = { active: 'green', pending: 'orange', completed: 'blue-grey', canceled: 'red' };
            return map[status] || 'grey';
        },

        getRoundStatusText(status) {
            const map = { active: '진행중', pending: '대기(3분)', completed: '종료', canceled: '취소' };
            return map[status] || status;
        }
    },

    watch: {
        '$store.state.selected_game_package': function () {
            this.loadEventConfig();
            this.loadRounds();
            this.loadCurrentRound();
        }
    }
}
</script>

<style scoped>
.tab-item {
    text-transform: none !important;
}
.tab-text {
    font-size: 13px;
    font-weight: 600;
}
@media (max-width: 600px) {
    .v-toolbar__content {
        flex-wrap: wrap;
        height: auto !important;
    }
    .tab-text {
        font-size: 11px;
    }
}
</style>
